/**
 * API client for Cogsmith backend
 */
import axios from 'axios';

// Use relative URL in production (works with both cogsmith.ai and www.cogsmith.ai)
// Use localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080');

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (email, password, name) =>
    api.post('/api/auth/register', { email, password, name }),
  
  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),
  
  requestPasswordReset: (email) =>
    api.post('/api/auth/request-password-reset', { email }),
  
  resetPassword: (resetToken, newPassword) =>
    api.post('/api/auth/reset-password', { reset_token: resetToken, new_password: newPassword }),

  verifyEmail: (token) =>
    api.post('/api/auth/verify-email', { token }),

  resendVerification: (email) =>
    api.post('/api/auth/resend-verification', { email }),

  getMe: () =>
    api.get('/api/auth/me'),
};

// Customer API
export const customerAPI = {
  getLicenses: () =>
    api.get('/api/customer/licenses'),
  
  getLicense: (licenseId) =>
    api.get(`/api/customer/licenses/${licenseId}`),
  
  getLicenseDevices: (licenseId) =>
    api.get(`/api/customer/licenses/${licenseId}/devices`),
  
  deactivateDevice: (licenseId, deviceId) =>
    api.delete(`/api/customer/licenses/${licenseId}/devices/${deviceId}`),
  
  getPurchases: () =>
    api.get('/api/customer/purchases'),
  
  getStats: () =>
    api.get('/api/customer/stats'),
};

// Checkout API
export const checkoutAPI = {
  createSession: (email) =>
    api.post('/api/checkout/create-session', { customer_email: email }),
  
  getSession: (sessionId) =>
    api.post('/api/checkout/get-session', { session_id: sessionId }),
  
  getConfig: () =>
    api.get('/api/checkout/config'),
};

// License API (for desktop app)
export const licenseAPI = {
  validate: (licenseKey) =>
    api.post('/api/license/validate', { license_key: licenseKey }),
  
  activateDevice: (licenseKey, deviceId, deviceName, platform, appVersion) =>
    api.post('/api/license/activate-device', {
      license_key: licenseKey,
      device_id: deviceId,
      device_name: deviceName,
      platform: platform,
      app_version: appVersion,
    }),
  
  deactivateDevice: (licenseKey, deviceId) =>
    api.post('/api/license/deactivate-device', {
      license_key: licenseKey,
      device_id: deviceId,
    }),
  
  listDevices: (licenseKey) =>
    api.post('/api/license/list-devices', { license_key: licenseKey }),
};

// Auth helpers
export const auth = {
  setToken: (token) => {
    localStorage.setItem('auth_token', token);
  },
  
  getToken: () => {
    return localStorage.getItem('auth_token');
  },
  
  removeToken: () => {
    localStorage.removeItem('auth_token');
  },
  
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },
  
  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
  
  removeUser: () => {
    localStorage.removeItem('user');
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('auth_token');
  },
  
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },
};

// Admin API methods
export const admin = {
  // License management
  createLicense: (email, tier, expiresInDays) =>
    api.post('/api/admin/licenses', { email, tier, expires_in_days: expiresInDays }),

  listLicenses: (params) =>
    api.get('/api/admin/licenses', { params }),

  revokeLicense: (licenseId, reason) =>
    api.post(`/api/admin/licenses/${licenseId}/revoke`, { reason }),

  bulkRevokeLicenses: (licenseIds, reason) =>
    api.post('/api/admin/licenses/bulk-revoke', { license_ids: licenseIds, reason }),

  // User management
  listUsers: (params) =>
    api.get('/api/admin/users', { params }),

  getUser: (userId) =>
    api.get(`/api/admin/users/${userId}`),

  // Analytics
  getAnalytics: () =>
    api.get('/api/admin/analytics/stats'),

  // Email management
  listEmails: (params) =>
    api.get('/api/admin/emails', { params }),

  getEmail: (emailId) =>
    api.get(`/api/admin/emails/${emailId}`),

  sendEmail: (emailData) =>
    api.post('/api/admin/emails/send', emailData),

  updateEmail: (emailId, updates) =>
    api.patch(`/api/admin/emails/${emailId}`, updates),

  deleteEmail: (emailId, permanent = false) =>
    api.delete(`/api/admin/emails/${emailId}`, { params: { permanent } }),

  getUnreadCounts: () =>
    api.get('/api/admin/emails/stats/unread-counts'),

  getEmailThread: (emailId) =>
    api.get(`/api/admin/emails/${emailId}/thread`),

  processEmailWithAI: (emailId) =>
    api.post(`/api/admin/emails/${emailId}/process`),

  // Email activity logs
  getEmailActivity: (emailId) =>
    api.get(`/api/admin/emails/${emailId}/activity`),

  getAllEmailActivity: (params) =>
    api.get('/api/admin/emails/activity/all', { params }),

  // Email rules
  listEmailRules: (params) =>
    api.get('/api/admin/email-rules', { params }),

  createEmailRule: (ruleData) =>
    api.post('/api/admin/email-rules', ruleData),

  updateEmailRule: (ruleId, updates) =>
    api.patch(`/api/admin/email-rules/${ruleId}`, updates),

  deleteEmailRule: (ruleId) =>
    api.delete(`/api/admin/email-rules/${ruleId}`),
};

export default api;

